<template>
  <v-card dark class="ModalAlert">
    <h2 class="ModalAlert__title">{{ title }}</h2>

    <div v-if="showSpiner" class="spinner">
      <FlamePreloader :compact="true" :size="100" />
    </div>
    <div v-else>
      <div v-html="text" class="ModalAlert__label"></div>
      <v-form v-if="confirmString" @submit.prevent="doAction">
        <v-text-field
          v-model="confirmLocal"
          class="ModalAlert__input"
          autofocus
          hide-details
          filled
          dense
        />
      </v-form>
      <div class="ModalAlert__footer">
        <v-btn color="n5" v-if="showCancelBtn" @click="close"> Cancel </v-btn>
        <v-btn
          @click="doAction"
          :disabled="disableAction"
          :color="confirmString && alertMessage ? 'r1' : 'y1'"
        >
          {{ actionBtnLabel }}
        </v-btn>
      </div>
    </div>
  </v-card>
</template>

<script>
import FlamePreloader from '@/components/_Common/FlamePreloader'
export default {
  name: 'ModalAlert',
  components: { FlamePreloader },
  props: {
    title: String,
    text: String,
    actionBtnLabel: {
      type: String,
      default: 'OK',
    },
    showCancelBtn: Boolean,
    withSpiner: {
      type: Boolean,
      default: true,
    },
    withErrCatch: {
      type: Boolean,
      default: false,
    },
    action: Function,
    cancelAction: Function,
    confirmString: String,
    alertMessage: {
      type: Boolean,
      default: true,
    },
    isError: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      confirmLocal: '',
      showSpiner: false,
    }
  },
  computed: {
    disableAction() {
      return this.confirmString && this.confirmLocal != this.confirmString
    },
  },
  methods: {
    async doAction() {
      if (this.disableAction) return

      if (this.action) {
        if (this.withSpiner) this.showSpiner = true
        let res = await this.action()
        setTimeout(() => this.$emit('close'), this.withSpiner ? 2000 : 0)
      } else this.$emit('close')
    },
    close() {
      this.showSpiner = false
      if (this.cancelAction) this.cancelAction()
      if (this.withErrCatch) throw 'closed'
      this.$emit('close')
    },
  },
}
</script>

<style lang="scss">
$style: ModalAlert;
.#{$style} {
  &.theme--dark.v-card {
    @extend %modal-body-styles;
    min-width: 400px;
    padding: 24px 24px 0;

    @media screen and (max-width: 600px) {
      min-width: 300px;
    }
  }
  &__title {
    font-weight: 500;
    font-size: $H16;
    color: $white;
    margin-bottom: 16px;
  }
  &__label {
    @extend %label-styles;
  }
  &__input {
    &.v-text-field--filled.v-input--dense > .v-input__control > .v-input__slot {
      @extend %input-styles;
    }
    &.v-input input {
      color: $white;
    }
  }
  &__footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 16px 0;
    gap: 16px;
    & > .theme--dark.v-btn {
      color: $white;
    }
  }
  .error-main {
    background: #e94a53;
    border-radius: 2px;
  }
  .spinner {
    display: flex;
    width: 100%;
    height: 100%;
    align-items: center;
    margin-bottom: 15px;
    justify-content: center;
  }

  // Team workspace benefits modal styles
  .team-workspace-benefits {
    .benefits-intro {
      margin-bottom: 24px;
      
      p {
        color: #a4a4ae;
        font-size: 14px;
        line-height: 1.5;
        margin: 0;
      }
    }
    
    .benefits-list {
      margin-bottom: 24px;
    }
    
    .benefit-item {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      margin-bottom: 16px;
      
      &:last-child {
        margin-bottom: 0;
      }
    }
    
    .benefit-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      background: rgba(255, 126, 33, 0.1);
      border-radius: 6px;
      flex-shrink: 0;
      
      i {
        color: #ff7e21;
        font-size: 18px;
      }
    }
    
    .benefit-content {
      display: flex;
      flex-direction: column;
      gap: 2px;
      
      strong {
        color: white;
        font-size: 14px;
        font-weight: 500;
        line-height: 1.3;
      }
      
      span {
        color: #a4a4ae;
        font-size: 12px;
        line-height: 1.4;
      }
    }
    
    .enterprise-section {
      margin-bottom: 20px;
      padding: 16px;
      background: rgba(100, 149, 237, 0.08);
      border: 1px solid rgba(100, 149, 237, 0.2);
      border-radius: 8px;
    }
    
    .enterprise-content {
      display: flex;
      align-items: center;
      gap: 12px;
    }
    
    .enterprise-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      background: rgba(100, 149, 237, 0.15);
      border-radius: 6px;
      flex-shrink: 0;
      
      i {
        color: #6495ed;
        font-size: 18px;
      }
    }
    
    .enterprise-text {
      display: flex;
      flex-direction: column;
      gap: 2px;
      flex-grow: 1;
      
      strong {
        color: white;
        font-size: 14px;
        font-weight: 500;
        line-height: 1.3;
      }
      
      span {
        color: #a4a4ae;
        font-size: 12px;
        line-height: 1.4;
      }
    }
    
    .enterprise-cta {
      flex-shrink: 0;
    }
    
    .enterprise-demo-btn {
      background: linear-gradient(135deg, #6495ed 0%, #7ba7f7 100%);
      color: white;
      border: none;
      padding: 6px 12px;
      border-radius: 6px;
      font-size: 11px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      
      &:hover {
        background: linear-gradient(135deg, #7ba7f7 0%, #8bb8ff 100%);
        transform: translateY(-1px);
      }
    }

    .upgrade-note {
      padding-top: 16px;
      border-top: 1px solid #2e2c42;
      
      p {
        color: #a4a4ae;
        font-size: 13px;
        margin: 0;
        text-align: center;
      }
    }
  }
}
</style>
